import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Pagination,
  Thumbs,
  FreeMode,
  Swiper as SwiperClass,
} from 'swiper';
import 'swiper/css/bundle';
import FullscreenImageSlider from '../FullscreenImageSlider/FullscreenImageSlider';
import { useMemo } from 'react';
import SliderImage from '../../Types/SliderImage';

interface ProductPageImageSliderProps {
  images: SliderImage[];
}

const THUMBNAILS_PER_VIEW = 8;

const ProductPageImageSlider = ({
  images,
}: ProductPageImageSliderProps) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<
    SwiperClass | undefined
  >();
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [rendered, setRendered] = useState(false);
  const imageCount = useMemo(
    () => (images && images?.length) || 0,
    [images]
  );
  useEffect(() => setRendered(true), []);

  return (
    <div className="relative pb-8 md:pb-0 [&_.swiper-pagination]:!-bottom-7 [&_.swiper-pagination]:md:hidden">
      {!rendered && images?.length > 0 && (
        <div className="mainSwiper-Placeholder">
          <img
            className="image"
            style={{ margin: 'auto' }}
            src={images[0]?.src}
            alt={images[0]?.alt}
            itemProp="image"
          />
        </div>
      )}
      {rendered && (
        <>
          <Swiper
            pagination={{
              dynamicBullets: imageCount > 8 ? true : false,
              dynamicMainBullets: 3,
            }}
            thumbs={{ swiper: thumbsSwiper }}
            modules={[FreeMode, Pagination, Thumbs]}
            lazy={true}
            loop={true}
            slidesPerView={1}
            className="!overflow-x-clip !overflow-y-visible border border-gray-100"
            onSlideChange={(swiper) =>
              setActiveSlideIndex(swiper.realIndex)
            }
          >
            {images?.map((image, index) => (
              <SwiperSlide
                key={image.src + index}
                onClick={() => setFullscreenOpen(true)}
              >
                <img
                  className="aspect-square h-full w-full object-contain"
                  src={image.src}
                  alt={image.alt}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          <Swiper
            onSwiper={setThumbsSwiper}
            spaceBetween={THUMBNAILS_PER_VIEW}
            watchSlidesProgress={true}
            slidesPerView={
              imageCount > THUMBNAILS_PER_VIEW
                ? THUMBNAILS_PER_VIEW
                : imageCount
            }
            slidesPerGroup={
              imageCount > THUMBNAILS_PER_VIEW
                ? THUMBNAILS_PER_VIEW
                : imageCount
            }
            modules={[FreeMode, Thumbs]}
            className="hidden md:block"
          >
            {images?.map((image, index) => (
              <SwiperSlide
                className="mt-2 aspect-square object-contain md:max-h-8 md:max-w-[32px] lg:max-h-12 lg:max-w-[48px] xl:max-h-20 xl:max-w-[80px]"
                key={image.thumbnailSrc + index}
              >
                <img
                  className="aspect-square h-full w-full border border-gray-100 object-contain"
                  src={image.thumbnailSrc}
                  alt={image.alt}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </>
      )}
      {fullscreenOpen && (
        <FullscreenImageSlider
          images={images}
          onClosed={() => setFullscreenOpen(false)}
          initialSlide={activeSlideIndex}
        />
      )}
    </div>
  );
};

export default ProductPageImageSlider;
